/**
 * With the default react-router's search params first argument (search),
 * when we have a url like: /books?title=Node&rating=5, we'd have to obtain
 * the query paramaters and values using method calls like:
 * const title = search.get("title");
 * const rating = search.get("rating");
 *
 * Our custom search params wraps react-router's search params and
 * returns and object that lets us query for properties directly:
 * const title = query.title
 * const rating = query.rating
 *
 * Our custom implementation also takes an object with optional transformation functions.
 * It iterates through each transformation function
 * and if it finds a match between transformation function and search param
 * it applies the function onto the search param.
 *
 * For example, we could do something like this:
 * const transformers = {
 *   parseAsBoolean: (string = '') => string === 'true',
 *   parseAsNumber: (string = '') => (string ? Number(string) : null),
 *   // other transformation functions to map all data types
 * };
 *
 * @credits: https://www.robinwieruch.de/react-router-search-params/
 */

import { useSearchParams } from "react-router";

export function parseAsBoolean(input: string) {
  return Boolean(input);
}

export function parseAsNumber(input: string) {
  const num = Number(input);

  return num ?? null;
}

export function parseAsString(input: string) {
  return (input ? String(input) : "");
}

/**
 * import useQuery, { parseAsBoolean, parseAsNumber, parseAsString } from "hooks/useQuery";
 * const [{ page, limit, isLastPage }] = useQuery({ 
 *   page: parseAsNumber, 
 *   limit: parseAsNumber, 
 *   isLastPage: parseAsBoolean
 *   // ... every other is parsed as string (by default)
 * }) 
 */
export default function useQuery(transformers = {}) {
  const [search, setSearch] = useSearchParams();
  const searchAsObject = Object.fromEntries(new URLSearchParams(search));

  const transformedSearch = Object.keys(transformers).reduce((acc, key) => {
    return {
      ...acc,
      [key]: transformers[key](acc[key]),
    }
  }, searchAsObject);

  return [transformedSearch, setSearch] as const;
}
